import React from 'react';
import { Sparkles, Shield, Zap } from 'lucide-react';
import ContactForm from './components/ContactForm';

function App() {
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-black/50 backdrop-blur-md z-50 border-b border-gray-700">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img src="/PRIVATELANE LOGO.png" alt="PrivateLane Logo" className="h-8 md:h-10 w-auto" />
            <span className="text-lg md:text-2xl font-bold">My PrivateLane</span>
          </div>
          <a 
            href="#contact" 
            className="bg-blue-600 hover:bg-blue-700 px-4 py-2 md:px-6 md:py-2 rounded-lg transition-all duration-300 font-semibold text-sm md:text-base whitespace-nowrap"
          >
            Get Started
          </a>
        </div>
      </header>

      {/* Hero Section with Black Car Background */}
      <section className="relative pt-56 pb-32 px-6 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/benz2.jpeg" 
            alt="Luxury Black Car"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black"></div>
        </div>
        
        {/* Content */}
        <div className="container mx-auto text-center max-w-5xl relative z-10">
          <h1 className="text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-blue-100 to-blue-300 bg-clip-text text-transparent leading-tight">
            Your Own Black Car Booking Platform
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-10 max-w-3xl mx-auto leading-relaxed">
            My PrivateLane helps you turn your riders into repeat private clients — and you keep 100% of what you charge.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="#contact"
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 px-10 py-5 rounded-xl text-lg font-bold transition-all duration-300 shadow-2xl hover:shadow-blue-500/50 transform hover:scale-105"
            >
              Start Your Platform
            </a>
            <a 
              href="#features"
              className="bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 px-10 py-5 rounded-xl text-lg font-semibold transition-all duration-300"
            >
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* Features Section with Car Image */}
      <section id="features" className="relative py-24 px-6">
        {/* Subtle Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900 to-black"></div>
        
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4">Everything You Need to Succeed</h2>
            <p className="text-xl text-gray-400">Professional tools for your luxury car service</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <FeatureCard 
              icon={<Sparkles className="w-12 h-12 text-blue-400" />}
              title="Custom Branding"
              description="Get your own customized booking platform with your brand colors, logo, and unique identity"
            />
            <FeatureCard 
              icon={<Shield className="w-12 h-12 text-green-400" />}
              title="Payment Integration"
              description="Stripe payment processing set up and ready to accept payments securely from day one"
            />
            <FeatureCard 
              icon={<Zap className="w-12 h-12 text-purple-400" />}
              title="Custom Domain"
              description="Choose between a custom domain or use yourname.myprivatelane.com subdomain"
            />
          </div>
          
          {/* Car Image Feature */}
          <div className="mt-20 relative rounded-2xl overflow-hidden max-w-5xl mx-auto">
            <img 
              src="/benz1.jpeg"
              alt="Luxury Black Car Interior"
              className="w-full h-80 object-cover opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center px-6">
                <h3 className="text-4xl font-bold mb-4">Premium Experience</h3>
                <p className="text-xl text-gray-300">Deliver exceptional service with a platform designed for luxury</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4">How It Works</h2>
            <p className="text-xl text-gray-400">Three simple steps to launch your platform</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <StepCard 
              number="1"
              title="Sign Up"
              description="Fill out the form below with your details and book a consultation call"
              image="/cadillac1.jpeg"
            />
            <StepCard 
              number="2"
              title="Customization"
              description="We'll set up your platform with your branding, payment processing, and domain"
              image="/cadillac2.jpeg"
            />
            <StepCard 
              number="3"
              title="Go Live"
              description="Start accepting bookings and grow your black car service business"
              image="/cadillac3.jpeg"
            />
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="relative py-24 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-black"></div>
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/cadillac4.jpeg"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto max-w-2xl relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold mb-4">Get Started Today</h2>
            <p className="text-xl text-gray-300">
              Fill out the form below and we'll be in touch with next steps to launch your platform
            </p>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-gray-800">
        <div className="container mx-auto text-center text-gray-400">
          <p>&copy; 2025 My PrivateLane. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="group bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm p-8 rounded-2xl border border-gray-700 hover:border-blue-500 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/20">
      <div className="mb-6 transform group-hover:scale-110 transition-transform duration-300">{icon}</div>
      <h3 className="text-2xl font-bold mb-4">{title}</h3>
      <p className="text-gray-300 leading-relaxed">{description}</p>
    </div>
  );
}

function StepCard({ number, title, description, image }) {
  return (
    <div className="group relative overflow-hidden rounded-2xl bg-gray-800/50 border border-gray-700 hover:border-blue-500 transition-all duration-500 hover:transform hover:scale-105">
      {/* Background Image */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={image} 
          alt={title}
          className="w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-110 transition-all duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-900"></div>
        <div className="absolute top-4 left-4 w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center text-2xl font-bold shadow-lg">
          {number}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-6">
        <h3 className="text-2xl font-bold mb-3">{title}</h3>
        <p className="text-gray-300 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

export default App;
